import { useEffect, useState } from 'react';
import { Clock, RefreshCw } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function relativeTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const ms = Date.now() - d.getTime();
    const sec = Math.max(0, Math.floor(ms / 1000));
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}d ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

/**
 * Last redeployed badge for the Owner Admin top bar.
 *
 * Surfaces the backend boot time (and an injected DEPLOY_TIMESTAMP if set),
 * the runtime environment, and short git SHA — so the owner can quickly tell
 * whether the preview pane and production are on the same build.
 */
export default function LastDeployedBadge() {
  const [info, setInfo] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await fetch(`${API_URL}/api/owner-admin/cost-dashboard/deploy-info`, {
          credentials: 'include',
        });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setInfo(data);
      } catch {
        /* fail silently — sidebar widget */
      }
    };
    fetchOnce();
    // Refresh deploy info every 5 minutes
    const fetchInterval = setInterval(fetchOnce, 300000);
    // Re-render every 30s so the "Xs ago" text stays live
    const tickInterval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => {
      cancelled = true;
      clearInterval(fetchInterval);
      clearInterval(tickInterval);
    };
  }, []);

  if (!info || (!info.deploy_at && !info.boot_at)) return null;

  const envColor =
    info.environment === 'production'
      ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
      : info.environment === 'preview'
      ? 'bg-violet-500/15 text-violet-400 border-violet-500/30'
      : 'bg-amber-500/15 text-amber-400 border-amber-500/30';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-white transition-colors cursor-help"
            data-testid="last-deployed-badge"
          >
            <Clock className="w-3.5 h-3.5" />
            <span className="font-mono">
              Deployed{' '}
              <span className="text-white font-medium" data-testid="last-deployed-relative">
                {relativeTime(info.deploy_at || info.boot_at)}
              </span>
            </span>
            {info.environment && (
              <Badge variant="outline" className={cn('text-[10px] uppercase', envColor)}>
                {info.environment}
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-sm">
          <div className="text-xs space-y-1.5">
            <div className="flex items-center gap-2 font-semibold">
              <RefreshCw className="w-3 h-3" />
              Deploy snapshot
            </div>
            <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-[11px]">
              <span className="text-muted-foreground">Environment</span>
              <span className="font-mono">{info.environment}</span>
              <span className="text-muted-foreground">Deploy time</span>
              <span className="font-mono">{info.deploy_at}</span>
              <span className="text-muted-foreground">Process boot</span>
              <span className="font-mono">{info.boot_at}</span>
              {info.git_sha && (
                <>
                  <span className="text-muted-foreground">Git SHA</span>
                  <span className="font-mono">{info.git_sha}</span>
                </>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground pt-1 border-t border-white/10 mt-2">
              Use this to confirm whether your preview pane and production are on the same build.
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
